import { useContext, useEffect } from "react";
import { Layout } from "antd";
import { observer } from "mobx-react-lite";
import { MSTContext } from "../../stores/main";
import { EazyLoader } from "../EazyLoader";
import { TextEffect } from "../ui/text-effect";
import diamondLoader from "../../assets/Loader_Studio_Diamond19.json";
import GalleryList from "./GalleryList/GalleryList";
import { GallerySidebar } from "./GallerySidebar/GallerySidebar";

import "./ProjectsGallery.scss";

export type IProjectInfo = {
  thumbnail: string;
  title: string;
  updatedAt: string;
  id: string;
  key?: string;
};

function ProjectsGallery() {
  const {
    platformStore: {
      allProjects,
      hasLoadedProjectList,
      isLoadingProjectList,
      loadProjects,
    },
    accountStore: { allAccountIds, loadStatus: accountLoadStatus },
    setCurrentTab,
  } = useContext(MSTContext);
  const accountCount = allAccountIds.length;
  const hasLoadedAnyProjects = allProjects.length > 0;
  const shouldShowGalleryList = hasLoadedProjectList || hasLoadedAnyProjects;

  useEffect(() => {
    setCurrentTab("");
  }, [setCurrentTab]);

  useEffect(() => {
    const canLoadProjects = accountLoadStatus === "loaded" && accountCount > 0;

    if (canLoadProjects && !hasLoadedProjectList && !isLoadingProjectList) {
      loadProjects().catch((error) =>
        console.error("Failed to load projects for gallery", error)
      );
    }
  }, [
    accountLoadStatus,
    accountCount,
    hasLoadedProjectList,
    isLoadingProjectList,
    loadProjects,
  ]);

  const userHasMultipleAccounts = allProjects.some(
    (p) => p.projectAccountId !== allProjects[0].projectAccountId
  );

  const pjOptions: IProjectInfo[] = allProjects.map((p) => ({
    title:
      p.title + (userHasMultipleAccounts ? ` | ${p.projectAccountId}` : ""),
    thumbnail: p.thumbS3Url,
    updatedAt: p.updatedAt,
    key: p.id,
    id: p.id,
  }));

  const _allprojects: IProjectInfo[] = JSON.parse(
    JSON.stringify(pjOptions)
  ).sort(function (a, b) {
    const aName = a.title ? a.title : a.thumbnail;
    const bName = b.title ? b.title : b.thumbnail;
    if (aName.toLocaleLowerCase() < bName.toLocaleLowerCase()) {
      return -1;
    } else {
      return 1;
    }
  });
  const projectsSortedByUpdateTime = JSON.parse(
    JSON.stringify(_allprojects)
  ).sort(function (a, b) {
    const aDate = new Date(a.updatedAt);
    const bDate = new Date(b.updatedAt);
    if (aDate < bDate) {
      return 1;
    } else {
      return -1;
    }
  });

  return (
    <Layout className="projects-gallery-layout">
      <GallerySidebar />
      <Layout.Content className="projects-gallery-content">
        <div className="project-galery-container">
          {!shouldShowGalleryList ? (
            <div className="loading-screen-content">
              <EazyLoader
                size={120}
                isLoop={true}
                customAnimation={diamondLoader}
              />
              <TextEffect
                per="char"
                preset="blur"
                className="loading-text-animated"
              >
                Loading your projects
              </TextEffect>
            </div>
          ) : (
            <GalleryList
              recentProjects={projectsSortedByUpdateTime}
              isLoadingProjects={isLoadingProjectList}
            />
          )}
        </div>
      </Layout.Content>
    </Layout>
  );
}

export default observer(ProjectsGallery);
